import { createFileRoute, Outlet, redirect } from '@tanstack/react-router';
import { getAppPlace, getCurrentAppState, throwForClosedAppPlace } from 'merchant-api/derived';
import { ErrorPage } from 'merchant-components';
import { getApplicationOpts, useApplication } from 'merchant-api/unified';
import { analytics } from 'merchant-components';
import { useEffect } from 'react';
import { Application } from 'merchant-api/types';

// Applicant email: data.email when present, otherwise the applicant's EMAIL_ADDRESS contact.
const getApplicantEmail = (applicationData?: Application | null): string | undefined => {
  const directEmail = (applicationData?.data as { email?: unknown } | undefined)?.email;
  if (typeof directEmail === 'string' && directEmail) return directEmail;

  return applicationData?.data?.party?.persons
    ?.flatMap((person) => person.contacts ?? [])
    .find((contact) => contact.type === 'EMAIL_ADDRESS' && contact.value)?.value;
};

const ApplicationLayout = () => {
  const { applicationData, applicationId } = useApplication();
  const applicantEmail = getApplicantEmail(applicationData);

  // ThreatMetrix device profiling. This layout is the single trigger: it only renders once the
  // /application guard has authenticated the visitor, and it covers every destination
  // (apply, renewal, offer, checkout) — not just the loan app wizard.
  useEffect(() => {
    if (applicationId) analytics.track('tm_analytics', applicationId, { plugins: { all: false, tm_analytics: true } });
  }, [applicationId]);

  useEffect(() => {
    const referralSource = applicationData?.metadata?.referralSource;
    if (referralSource || applicantEmail) {
      const userId = analytics.getUserId();
      analytics.identify(userId, {
        applicationId,
        ...(referralSource && { referralSource }),
        ...(applicantEmail && { email: applicantEmail }),
      });
    }
  }, [applicationId, applicantEmail]);

  return <Outlet />;
};

export const Route = createFileRoute('/application/$id')({
  // The app-state -> redirect logic lives in `loader`, NOT `beforeLoad`, on purpose.
  // Throwing a redirect from an async beforeLoad on a hard page load races with
  // TanStack Router loading the ancestor matches: the ancestors are left `pending`
  // with no loadPromise, so the router throws `undefined` during render and the page
  // goes blank. Loaders run after all beforeLoads settle, which avoids the race.
  loader: async ({ context, location, params }) => {
    const currentApp = await context.queryClient.fetchQuery(getApplicationOpts(params.id));
    if (!currentApp) throw redirect({ to: '/apply/landing' });

    // Terminal places (funded app -> home, dormant -> expired error) come from the shared
    // app-state mapper; the base and /apply paths skip the full place resolution.
    const applicationPath = `/application/${params.id}`;
    const pathname = location.pathname.replace(/\/$/, '');
    if (pathname === applicationPath || pathname === `${applicationPath}/apply`) {
      throwForClosedAppPlace(await getAppPlace(currentApp));
      return;
    }

    const result = await getCurrentAppState({ id: params.id });
    if (!result) throw redirect({ to: '/apply/landing' });

    const appState = result.state || 'apply';
    const statePath = `${applicationPath}/${appState}`;
    if (pathname === statePath) return;

    throw redirect({ to: `/application/$id/${appState}`, params: { id: result.id } });
  },
  context: ({ params }) => {
    return { applicationId: params.id };
  },
  errorComponent: (props) => <ErrorPage {...props} />,
  component: ApplicationLayout,
});
